import React, { useState, useEffect, useContext } from "react";
import { Table, Spin, Tooltip, Button, Progress, Modal, Tag, Space, Typography, Card } from "antd";
import { LoadingOutlined, PlusOutlined, CloseCircleOutlined, FileTextOutlined, RedoOutlined, CopyOutlined, PlayCircleOutlined, EditOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { adminContext } from "../../Common/PageRoute";
import { PageLayout, PageHeader, SectionCard, TableToolbar } from "../../Common/Layout";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import FyiImportService, { IFyiImportJob, FyiImportJobStatus } from "../../Services/FyiImportService";
import PracticesService from "../../Services/PracticesService";
import FyiImportForm from "./FyiImportForm";
import dayjs from "dayjs";

const { Text } = Typography;

interface FyiImportListState {
  items: IFyiImportJob[];
  totalItems: number;
}

const FyiImportList: React.FC = () => {
  const { openNotification } = useContext(adminContext);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [practices, setPractices] = useState<Record<number, string>>({});
  
  const [dataList, setDataList] = useState<FyiImportListState>({
    items: [],
    totalItems: 0,
  });

  const [dialog, setDialog] = useState<{ type: string; params: any }>({
    type: "",
    params: {},
  });

  const [listParams, setListParams] = useState({
    search: "",
    currentPage: 1,
    pageSize: 15,
  });

  const [logContent, setLogContent] = useState("");
  const [logLoading, setLogLoading] = useState(false);

  // Load practices list once for mapping IDs to names
  useEffect(() => {
    PracticesService.getPractice()
      .then((res: any) => {
        if (res?.status && res?.result) {
          const map: Record<number, string> = {};
          res.result.forEach((p: any) => {
            map[p.id] = p.name;
          });
          setPractices(map);
        }
      })
      .catch(() => {
        console.error("Failed to load practices mapping.");
      });
  }, []);

  const fetchItems = async (start: number, length: number, isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const res = await FyiImportService.getList(start, length, listParams.search);
      if (res?.status && res?.result) {
        setDataList({
          totalItems: res.result.totalRecords ?? 0,
          items: res.result.items ?? [],
        });
      } else if (!isSilent) {
        openNotification("error", res?.message || "Failed to fetch FYI Import jobs");
      }
    } catch (err) {
      if (!isSilent) openNotification("error", "Error fetching FYI Import jobs");
    } finally {
      if (!isSilent) setLoading(false);
    }
  };

  // Regular pagination / search fetch
  useEffect(() => {
    fetchItems(
      (listParams.currentPage - 1) * listParams.pageSize,
      listParams.pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, refresh]);

  // Polling setup: poll every 10 seconds if there are active jobs (Pending / InProgress) in the current view
  useEffect(() => {
    const hasActiveJobs = dataList.items.some(
      (item) =>
        item.status === FyiImportJobStatus.Pending ||
        item.status === FyiImportJobStatus.InProgress
    );

    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      fetchItems(
        (listParams.currentPage - 1) * listParams.pageSize,
        listParams.pageSize,
        true
      );
    }, 10000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataList.items]);

  const handleCancel = async (id: string) => {
    try {
      const res = await FyiImportService.cancel(id);
      if (res?.status) {
        openNotification("success", "FYI Import Job cancelled successfully.");
        setRefresh(!refresh);
      } else {
        openNotification("error", res?.message || "Failed to cancel job");
      }
    } catch (err) {
      openNotification("error", "Error cancelling job");
    }
  };

  const handleRestart = async (id: string) => {
    try {
      const res = await FyiImportService.restart(id);
      if (res?.status) {
        openNotification("success", "FYI Import Job restarted successfully.");
        setRefresh(!refresh);
      } else {
        openNotification("error", res?.message || "Failed to restart job");
      }
    } catch (err) {
      openNotification("error", "Error restarting job");
    }
  };

  const handleViewLog = async (id: string) => {
    setLogContent("");
    setDialog({ type: "viewLog", params: { id } });
    setLogLoading(true);
    try {
      const res = await FyiImportService.getLog(id);
      setLogContent(res || "Log is empty.");
    } catch (err: any) {
      setLogContent(err?.response?.data?.message || "Failed to retrieve log content.");
    } finally {
      setLogLoading(false);
    }
  };

  const handleCopyLog = () => {
    navigator.clipboard.writeText(logContent);
    openNotification("success", "Log content copied to clipboard!");
  };

  const getStatusTag = (record: IFyiImportJob) => {
    switch (record.status) {
      case FyiImportJobStatus.Pending:
        return <Tag color="blue">Pending</Tag>;
      case FyiImportJobStatus.InProgress:
        return <Tag color="orange" className="ant-tag-has-color">In progress</Tag>;
      case FyiImportJobStatus.Completed:
        return <Tag color="green">Completed</Tag>;
      case FyiImportJobStatus.Failed:
        return (
          <Tooltip title={record.errorDetails || "No error details available"}>
            <Tag color="red" style={{ cursor: "pointer" }}>Failed ⚠️</Tag>
          </Tooltip>
        );
      case FyiImportJobStatus.Cancelled:
        return <Tag color="default">Cancelled</Tag>;
      default:
        return <Tag>Unknown</Tag>;
    }
  };

  const columns: ColumnsType<IFyiImportJob> = [
    {
      title: "Practice",
      dataIndex: "practiceId",
      key: "PracticeId",
      width: "15%",
      render: (practiceId: number) => (
        <span>
          <strong>{practices[practiceId] || "Loading..."}</strong>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>ID: {practiceId}</Text>
        </span>
      ),
    },
    {
      title: "S3 Location",
      dataIndex: "bucketName",
      key: "BucketName",
      width: "20%",
      render: (_, record) => (
        <span style={{ wordBreak: "break-all" }}>
          <Text strong>{record.bucketName}</Text>
          {record.s3Prefix && (
            <>
              <br />
              <Text type="secondary" style={{ fontSize: 11 }}>Prefix: {record.s3Prefix}</Text>
            </>
          )}
        </span>
      ),
    },
    {
      title: "Region & Concurrency",
      key: "RegionConcurrency",
      width: "15%",
      render: (_, record) => (
        <span>
          <Tag color="geekblue">{record.region}</Tag>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Concurrency: <strong>{record.maxConcurrency || 8}</strong>
          </Text>
        </span>
      ),
    },
    {
      title: "Status",
      key: "Status",
      width: "10%",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Progress",
      key: "Progress",
      width: "25%",
      render: (_, record) => {
        const total = record.totalCount || 0;
        const success = record.successCount || 0;
        const skip = record.skipCount || 0;
        const error = record.errorCount || 0;
        const processed = success + skip + error;
        const percent = total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;

        let progressStatus: "normal" | "active" | "success" | "exception" = "normal";
        if (record.status === FyiImportJobStatus.InProgress) progressStatus = "active";
        else if (record.status === FyiImportJobStatus.Completed) progressStatus = "success";
        else if (record.status === FyiImportJobStatus.Failed) progressStatus = "exception";

        return (
          <Tooltip title={`Total: ${total} | Success: ${success} | Skips: ${skip} | Errors: ${error}`}>
            <div>
              <Progress percent={percent} size="small" status={progressStatus} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginTop: "4px" }}>
                <span>Success: <Text type="success">{success}</Text></span>
                <span>Skips: <Text type="warning">{skip}</Text></span>
                <span>Errors: <Text type="danger">{error}</Text></span>
              </div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Duration",
      key: "Duration",
      width: "15%",
      render: (_, record) => {
        const started = record.startedOn ? dayjs(record.startedOn) : null;
        const completed = record.completedOn ? dayjs(record.completedOn) : null;

        if (!started) return "-";
        return (
          <span style={{ fontSize: "12px" }}>
            Started: {started.format("DD/MM/YY HH:mm")}
            {completed && (
              <>
                <br />
                Finished: {completed.format("DD/MM/YY HH:mm")}
              </>
            )}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Space size="middle">
          {(record.status === FyiImportJobStatus.Pending || record.status === FyiImportJobStatus.InProgress) && (
            <Tooltip title="Cancel Job">
              <Button
                type="text"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setDialog({
                    type: "cancelConfirm",
                    params: { id: record.id, bucketName: record.bucketName },
                  });
                }}
              />
            </Tooltip>
          )}

          {record.status !== FyiImportJobStatus.Pending && (
            <Tooltip title="View Job Logs">
              <Button
                type="text"
                icon={<FileTextOutlined style={{ color: "#1890ff" }} />}
                onClick={() => handleViewLog(record.id)}
              />
            </Tooltip>
          )}

          {(record.status === FyiImportJobStatus.Failed || record.status === FyiImportJobStatus.Cancelled || record.status === FyiImportJobStatus.Completed) && (
            <Tooltip title="Restart Job (In-place)">
              <Button
                type="text"
                icon={<PlayCircleOutlined style={{ color: "#52c41a" }} />}
                onClick={() => {
                  setDialog({
                    type: "restartConfirm",
                    params: { id: record.id, bucketName: record.bucketName },
                  });
                }}
              />
            </Tooltip>
          )}

          {(record.status === FyiImportJobStatus.Failed || record.status === FyiImportJobStatus.Cancelled || record.status === FyiImportJobStatus.Completed) && (
            <Tooltip title="Edit Job">
              <Button
                type="text"
                icon={<EditOutlined style={{ color: "#fa8c16" }} />}
                onClick={() => {
                  setDialog({ type: "editJob", params: { record } });
                }}
              />
            </Tooltip>
          )}

          {(record.status === FyiImportJobStatus.Failed || record.status === FyiImportJobStatus.Cancelled) && (
            <Tooltip title="Clone as new Job">
              <Button
                type="text"
                icon={<CopyOutlined style={{ color: "#1890ff" }} />}
                onClick={() => {
                  setDialog({ type: "newJob", params: { record } });
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const primaryActions: any[] = [
    {
      label: "Enqueue S3 Import",
      icon: <PlusOutlined />,
      type: "primary",
      onClick: () => setDialog({ type: "newJob", params: {} }),
    },
  ];

  return (
    <PageLayout>
      <PageHeader title="FYI Bulk Import" subtitle="Manage FYI bulk import jobs" />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={(val: string) => setListParams({ ...listParams, search: val, currentPage: 1 })}
            searchPlaceholder="Search by bucket name or Practice ID..."
            onRefresh={() => setRefresh(!refresh)}
          />
        </div>
        <Table
          dataSource={dataList.items}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: window.innerHeight - 270 }}
          loading={{
            spinning: loading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} jobs`,
            current: listParams.currentPage,
            pageSize: listParams.pageSize,
            showSizeChanger: true,
            total: dataList.totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setListParams({
                ...listParams,
                currentPage: paging.current,
                pageSize: paging.pageSize,
              });
            }
          }}
        />

        <FyiImportForm
          visible={dialog.type === "newJob" || dialog.type === "editJob"}
          isEdit={dialog.type === "editJob"}
          initialValues={dialog.params?.record}
          onClose={(confirmed: boolean) => {
            setDialog({ type: "", params: {} });
            if (confirmed) {
              setRefresh(!refresh);
            }
          }}
        />

        {dialog.type === "cancelConfirm" && (
          <ConfirmationModal
            open={true}
            onNo={() => setDialog({ type: "", params: {} })}
            onYes={async () => {
              const { id } = dialog.params;
              await handleCancel(id);
              setDialog({ type: "", params: {} });
            }}
            text={`Are you sure you want to cancel the FYI import job running on bucket "${dialog.params.bucketName}"?`}
          />
        )}

        {dialog.type === "restartConfirm" && (
          <ConfirmationModal
            open={true}
            onNo={() => setDialog({ type: "", params: {} })}
            onYes={async () => {
              const { id } = dialog.params;
              await handleRestart(id);
              setDialog({ type: "", params: {} });
            }}
            text={`Are you sure you want to restart the FYI import job running on bucket "${dialog.params.bucketName}"? This will reset progress counts and run the import again.`}
          />
        )}

        <Modal
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 24 }}>
              <span>Job Execution Log</span>
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopyLog} disabled={logLoading || !logContent}>
                Copy Log
              </Button>
            </div>
          }
          open={dialog.type === "viewLog"}
          onCancel={() => setDialog({ type: "", params: {} })}
          footer={[
            <Button key="close" type="primary" onClick={() => setDialog({ type: "", params: {} })}>
              Close
            </Button>
          ]}
          width={800}
          destroyOnClose
        >
          {logLoading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
            </div>
          ) : (
            <Card
              styles={{ body: { padding: "12px", backgroundColor: "#1e1e1e", borderRadius: "6px" } }}
              style={{ marginTop: "12px" }}
            >
              <pre
                style={{
                  maxHeight: "450px",
                  overflowY: "auto",
                  color: "#d4d4d4",
                  fontFamily: "Consolas, Monaco, monospace",
                  fontSize: "12px",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {logContent}
              </pre>
            </Card>
          )}
        </Modal>
      </SectionCard>
    </PageLayout>
  );
};

export default FyiImportList;
